import React, { useState, useEffect } from 'react';

type AnnouncementAudience = 'all' | 'legacy' | 'new_tiers_all' | 'starter' | 'growth' | 'scale';

const AUDIENCE_OPTIONS: { value: AnnouncementAudience; label: string; description: string }[] = [
  { value: 'all', label: 'Everyone', description: 'All authenticated users (default)' },
  { value: 'legacy', label: 'Legacy plan only', description: 'Grandfathered £100 MarketMapr Pro / tier-less customers' },
  { value: 'new_tiers_all', label: 'All new tiers (Starter + Growth + Scale)', description: 'Any customer on a £249/£449/£749 plan' },
  { value: 'starter', label: 'Starter only (£249)', description: '' },
  { value: 'growth', label: 'Growth only (£449)', description: '' },
  { value: 'scale', label: 'Scale only (£749)', description: '' },
];

const audienceLabel = (a: AnnouncementAudience): string =>
  AUDIENCE_OPTIONS.find(o => o.value === a)?.label || a;

type EmailRecipientMode = 'all' | 'admins';

interface Announcement {
  id: number;
  title: string;
  body: string | null;
  storylane_url: string | null;
  audience: AnnouncementAudience;
  email_users: boolean;
  email_recipient_mode: EmailRecipientMode;
  emailed_at: string | null;
  email_recipient_count: number | null;
  is_active: boolean;
  seen_count: number;
  created_at: string;
}

interface SeenUser {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  dismissed_at: string;
}

interface SeenUsersResponse {
  seen: SeenUser[];
  seenCount: number;
  totalUsers: number;
  unseenCount: number;
}

const getToken = () => localStorage.getItem('token');
const authHeaders = (): HeadersInit => ({
  'Authorization': `Bearer ${getToken()}`,
  'Content-Type': 'application/json'
});

const AnnouncementManager: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [storylaneUrl, setStoryLaneUrl] = useState('');
  const [audience, setAudience] = useState<AnnouncementAudience>('all');
  const [emailUsers, setEmailUsers] = useState(false);
  const [emailRecipientMode, setEmailRecipientMode] = useState<EmailRecipientMode>('admins');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);

  // "Seen by" modal state
  const [seenModal, setSeenModal] = useState<{ announcement: Announcement | null; data: SeenUsersResponse | null; loading: boolean }>({
    announcement: null,
    data: null,
    loading: false
  });

  const openSeenModal = async (announcement: Announcement) => {
    setSeenModal({ announcement, data: null, loading: true });
    try {
      const res = await fetch(`/api/announcements/admin/${announcement.id}/seen-users`, {
        headers: authHeaders()
      });
      if (res.ok) {
        const data: SeenUsersResponse = await res.json();
        setSeenModal({ announcement, data, loading: false });
      } else {
        setSeenModal({ announcement, data: null, loading: false });
      }
    } catch (err) {
      console.error('Failed to fetch seen users:', err);
      setSeenModal({ announcement, data: null, loading: false });
    }
  };

  const closeSeenModal = () => setSeenModal({ announcement: null, data: null, loading: false });

  const loadAnnouncements = async () => {
    try {
      const res = await fetch('/api/announcements/admin/all', { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAnnouncements(data.announcements || []);
      }
    } catch (err) {
      console.error('Failed to load announcements:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const handleStartEdit = (a: Announcement) => {
    setEditingId(a.id);
    setTitle(a.title);
    setBody(a.body || '');
    setStoryLaneUrl(a.storylane_url || '');
    setAudience(a.audience || 'all');
    setEmailUsers(a.email_users === true);
    setEmailRecipientMode(a.email_recipient_mode || 'admins');
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setTitle(''); setBody(''); setStoryLaneUrl(''); setAudience('all');
    setEmailUsers(false); setEmailRecipientMode('admins');
    setError('');
  };

  const handleSave = async () => {
    if (!title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError('');
    try {
      const url = editingId
        ? `/api/announcements/admin/${editingId}`
        : '/api/announcements/admin';
      const method = editingId ? 'PATCH' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify({ title, body, storylaneUrl, audience, emailUsers, emailRecipientMode })
      });
      if (res.ok) {
        handleCancelForm();
        loadAnnouncements();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to save');
      }
    } catch (err) {
      setError('Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this announcement? Users who haven\'t seen it yet will never see it.')) return;
    try {
      await fetch(`/api/announcements/admin/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      loadAnnouncements();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 tracking-tight">Feature Announcements</h2>
          <p className="text-sm text-gray-500 mt-1">
            Create announcements that show as modals to users on their next visit.
          </p>
        </div>
        <button onClick={() => showForm ? handleCancelForm() : setShowForm(true)} className="btn-primary">
          {showForm ? 'Cancel' : '+ New Announcement'}
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="card p-6 animate-slide-in-up">
          <h3 className="text-base font-semibold text-gray-900 mb-4">{editingId ? 'Edit Announcement' : 'New Announcement'}</h3>

          {error && (
            <div className="bg-red-50 border border-red-200/60 rounded-xl p-3 mb-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. New Card View Available"
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Audience</label>
              <select
                value={audience}
                onChange={e => setAudience(e.target.value as AnnouncementAudience)}
                className="input"
              >
                {AUDIENCE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                {AUDIENCE_OPTIONS.find(o => o.value === audience)?.description || ''}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Description <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="Describe the new feature..."
                rows={4}
                className="input min-h-[100px] resize-y"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Storylane Demo <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={storylaneUrl}
                onChange={e => setStoryLaneUrl(e.target.value)}
                placeholder={'<div>\n  <script async src="https://js.storylane.io/js/v2/storylane.js"></script>\n  <div class="sl-embed" style="..."><iframe src="https://app.storylane.io/demo/...?embed=popup" ...></iframe></div>\n</div>'}
                rows={6}
                className="input min-h-[120px] resize-y font-mono text-xs"
              />
              <p className="text-xs text-gray-400 mt-1">
                Paste the full embed snippet from Storylane (Share → Embed → Copy code) for the correct aspect ratio and popup mode. A plain URL still works as a fallback.
              </p>
            </div>

            <div className="border-t border-gray-200 pt-4 mt-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailUsers}
                  onChange={e => setEmailUsers(e.target.checked)}
                  disabled={!!editingId && /* once emails have already been dispatched we don't re-send */
                    announcements.find(a => a.id === editingId)?.emailed_at != null}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-700">Email recipients about this announcement</div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Sent via Mailgun. Excludes cancelled customers. Past_due / unpaid still receive it.
                    {editingId && announcements.find(a => a.id === editingId)?.emailed_at && (
                      <> Already dispatched on {new Date(announcements.find(a => a.id === editingId)!.emailed_at!).toLocaleDateString()} — can't re-send.</>
                    )}
                  </p>
                </div>
              </label>

              {emailUsers && (
                <div className="mt-3 pl-6 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="emailRecipientMode"
                      checked={emailRecipientMode === 'admins'}
                      onChange={() => setEmailRecipientMode('admins')}
                    />
                    <span className="text-sm text-gray-700">
                      Admins only <span className="text-gray-400">(one email per team; solo users count as their own admin)</span>
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="emailRecipientMode"
                      checked={emailRecipientMode === 'all'}
                      onChange={() => setEmailRecipientMode('all')}
                    />
                    <span className="text-sm text-gray-700">
                      All team members <span className="text-gray-400">(every user, including team members on team plans)</span>
                    </span>
                  </label>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={handleCancelForm} className="btn-secondary">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Publish Announcement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Announcements list */}
      {loading ? (
        <div className="card p-8 text-center text-gray-500 text-sm">Loading...</div>
      ) : announcements.length === 0 ? (
        <div className="card p-12 text-center animate-fade-in">
          <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
          </div>
          <p className="text-sm text-gray-500">No announcements yet. Create one to notify all users.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map(a => (
            <div key={a.id} className="card p-5 card-hover">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-base font-semibold text-gray-900">{a.title}</h3>
                    {a.is_active ? (
                      <span className="badge badge-green">Active</span>
                    ) : (
                      <span className="badge badge-gray">Inactive</span>
                    )}
                    <span className="badge badge-gray" title="Who this announcement is shown to">
                      {audienceLabel(a.audience || 'all')}
                    </span>
                    {a.emailed_at && (
                      <span className="badge badge-blue" title={`Dispatched ${new Date(a.emailed_at).toLocaleString()}`}>
                        Emailed {a.email_recipient_count ?? 0}
                      </span>
                    )}
                  </div>
                  {a.body && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">{a.body}</p>
                  )}
                  {a.storylane_url && (
                    <p className="text-xs text-blue-600 mb-2">Has Storylane demo attached</p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>{new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    <button
                      onClick={() => openSeenModal(a)}
                      className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                      title="See which users have viewed this"
                    >
                      · {a.seen_count} {a.seen_count === 1 ? 'user' : 'users'} seen
                    </button>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleStartEdit(a)}
                    className="btn-ghost text-xs"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(a.id)}
                    className="btn-ghost text-xs text-red-600 hover:bg-red-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Seen-by modal */}
      {seenModal.announcement && (
        <div className="modal-backdrop flex items-center justify-center" onClick={closeSeenModal}>
          <div className="modal-dialog max-w-lg w-full mx-4 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200/80 flex items-start justify-between">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900">Who's seen this?</h3>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{seenModal.announcement.title}</p>
              </div>
              <button
                onClick={closeSeenModal}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none ml-3"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {seenModal.loading ? (
                <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
              ) : !seenModal.data ? (
                <div className="p-8 text-center text-sm text-red-500">Failed to load.</div>
              ) : seenModal.data.seen.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500">
                  Nobody has seen this announcement yet.
                  {seenModal.data.totalUsers > 0 && (
                    <p className="text-xs text-gray-400 mt-1">{seenModal.data.totalUsers} users haven't seen it.</p>
                  )}
                </div>
              ) : (
                <>
                  <div className="px-6 py-3 bg-gray-50/60 border-b border-gray-200/80 flex items-center justify-between">
                    <p className="text-xs font-medium text-gray-700">
                      {seenModal.data.seenCount} of {seenModal.data.totalUsers} users
                    </p>
                    {seenModal.data.unseenCount > 0 && (
                      <p className="text-xs text-gray-500">
                        {seenModal.data.unseenCount} haven't seen it yet
                      </p>
                    )}
                  </div>
                  <ul className="divide-y divide-gray-100">
                    {seenModal.data.seen.map(u => {
                      const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
                      return (
                        <li key={u.id} className="px-6 py-3 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{fullName}</p>
                            {fullName !== u.email && (
                              <p className="text-xs text-gray-500 truncate">{u.email}</p>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 flex-shrink-0">
                            {new Date(u.dismissed_at).toLocaleString('en-GB', {
                              day: 'numeric', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200/80 flex justify-end">
              <button onClick={closeSeenModal} className="btn-secondary text-sm">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementManager;
